import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { MessageSquare, Plus, Heart, MessageCircle, Clock, User, Building, Search, ChevronLeft, ChevronRight, Pin } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Forum = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'general' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, page]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/forum/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (selectedCategory && selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/forum/posts`, { params, headers });
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setPage(1);
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const handleCreatePost = async () => {
    if (!user) {
      toast.error('Please log in to create a post');
      navigate('/login');
      return;
    }

    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/forum/posts`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post created successfully!');
      setNewPostOpen(false);
      setNewPost({ title: '', content: '', category: 'general' });
      navigate(`/forum/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating post');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const getCategoryColor = (value) => {
    const colors = {
      general: 'bg-secondary text-secondary-foreground',
      home_care: 'bg-blue-500/20 text-blue-400',
      senior_living: 'bg-green-500/20 text-green-400',
      job_board: 'bg-purple-500/20 text-purple-400',
      events: 'bg-gold/20 text-gold',
      resources: 'bg-orange-500/20 text-orange-400',
    };
    return colors[value] || 'bg-secondary text-secondary-foreground';
  };

  const filteredPosts = posts.filter(post => 
    searchQuery === '' || 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
          <div>
            <p className="overline text-gold mb-4">Community</p>
            <h1 className="text-3xl md:text-4xl flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-gold" />
              Forum
            </h1>
            <p className="text-muted-foreground mt-2">
              Connect with fellow senior care professionals
            </p>
          </div>
          <Button 
            onClick={() => user ? setNewPostOpen(true) : navigate('/login')}
            className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm mt-6 md:mt-0"
            data-testid="new-post-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              className="pl-10 rounded-sm"
              data-testid="search-posts"
            />
          </div>
          <Select value={selectedCategory} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-full md:w-[200px] rounded-sm" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Category Quick Links */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => handleCategoryChange(cat.value)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                selectedCategory === cat.value 
                  ? 'bg-gold text-primary' 
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20 border border-border/50 bg-card">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground mb-4">No posts found</p>
            {user && (
              <Button onClick={() => setNewPostOpen(true)} variant="outline">
                Be the first to post
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/forum/${post.id}`}
                className="block border border-border/50 bg-card p-5 hover:border-gold/50 transition-colors"
                data-testid={`post-${post.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {post.is_pinned && (
                        <Pin className="w-4 h-4 text-gold" />
                      )}
                      <span className={`px-2 py-0.5 text-xs uppercase tracking-wider rounded ${getCategoryColor(post.category)}`}>
                        {getCategoryLabel(post.category)}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium mb-2 truncate">{post.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {post.content}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {post.author_name}
                      </span>
                      {post.author_company_type && (
                        <span className="flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          {post.author_company_type}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(parseISO(post.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {post.reply_count}
                    </div>
                    <div className="flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      {post.like_count}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* New Post Dialog */}
      <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={newPost.category} 
                onValueChange={(v) => setNewPost(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="new-post-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={newPost.title}
                onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter post title..."
                data-testid="new-post-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your post..."
                rows={8}
                data-testid="new-post-content"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPostOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePost}
              disabled={submitting}
              className="bg-gold text-primary hover:bg-gold/90"
              data-testid="submit-post-btn"
            >
              {submitting ? 'Posting...' : 'Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Forum;
